import { ActionContext } from "vuex/types";
import { General } from "@/utils/constants";
import { downloadWithURL } from "@/utils/fileUtility";
import { SnackQueue as Queue } from "@/plugins/SnackQueue";
import { DOE_TYPE_ACTIONS as doeAction } from "@/utils/enumerate";

import {
  ResponsePaged,
  ResponseType,
  ResponseGenericType,
  FilterPaged
} from "@/store/types/types";

import {
  getPendingList,
  getProgrammedList,
  getPublishedList,
  getRefusedList,
  refusePublish,
  programPublish,
  publish,
  doeReportListFile,
  setToSentToPrintshop,
  createModelDocumentEdoweb,
  updateModelDocumentEdoweb,
  listModelDocumentEdoweb,
  listModelDocumentEdowebAdm,
  getModelDocumentEdoweb,
  deleteModelDocumentEdoweb,
  activeModelDocumentEdoweb,
  validateDocumentEdoweb
} from "@/services/api/ApiDOE";

import {
  ICompleteDOE,
  IDocumentDOE,
  IActiveDOE,
  IRequestPageDOE,
  ISimpleDOE,
  IUpdateDOE,
  IReportListDOE,
  ISendToPrintshopDOE,
  IModelDOE,
  ITableValidateDOE,
  IListDOE,
  IDOERefused,
  IDOEPublished,
  IDOEProgrammed,
  IDOEPending
} from "@/store/types/doe-types";

import { IDownloadFileBatch } from "@/store/types/file-type";
import { showErrorMessage, showSuccessMessage } from "@/utils/messageUtility";
import { RootState } from "@/store/types/root";

const state = {
  pendingList: [] as IModelDOE[],
  pendingListCount: 0,
  programmedList: [] as IModelDOE[],
  programmedListCount: 0,
  refusedList: [] as IModelDOE[],
  refusedListCount: 0,
  publishedList: [] as IModelDOE[],
  publishedListCount: 0,
  documentEdoweb: [] as ICompleteDOE[],
  documentEdowebCount: 0,
  documentEdowebAdm: [] as ICompleteDOE[],
  documentEdowebAdmCount: 0,
  currentDocumentEdoweb: {} as ICompleteDOE,
  doeSnackList: [] as IDocumentDOE[],
  doePageNeedsUpdate: 0
};

type State = typeof state;

const mutations = {
  setPendingList: (state: State, data: ResponsePaged<IModelDOE>) => {
    state.pendingList = data.results;
    state.pendingListCount = data.count;
  },

  setProgrammedList: (state: State, data: ResponsePaged<IModelDOE>) => {
    state.programmedList = data.results;
    state.programmedListCount = data.count;
  },

  setRefusedList: (state: State, data: ResponsePaged<IModelDOE>) => {
    state.refusedList = data.results;
    state.refusedListCount = data.count;
  },

  setPublishedList: (state: State, data: ResponsePaged<IModelDOE>) => {
    state.publishedList = data.results;
    state.publishedListCount = data.count;
  },

  setListDocumentEdoweb: (state: State, data: ResponsePaged<ICompleteDOE>) => {
    state.documentEdoweb = data.results;
    state.documentEdowebCount = data.count;
  },

  setListDocumentEdowebAdm: (
    state: State,
    data: ResponsePaged<ICompleteDOE>
  ) => {
    state.documentEdowebAdm = data.results;
    state.documentEdowebAdmCount = data.count;
  },

  setCurrentDocumentEdoweb: (state: State, data: ICompleteDOE) => {
    state.currentDocumentEdoweb = data;
  },

  setDoeSnackList: (state: State, doeList: IDocumentDOE[]) => {
    if (Array.isArray(doeList)) {
      doeList.forEach(object => state.doeSnackList.push(object));
    }
  },

  clearDoeSnackList: (state: State) => {
    state.doeSnackList = [];
  },

  checkDoneDoeActions: (state: State) => {
    const hasPendingDoe = state.doeSnackList.some(
      doe => doe.status === "pending" || doe.status === "processing"
    );
    if (!hasPendingDoe) {
      state.doePageNeedsUpdate = Date.now();
      Queue.open({
        id: "doe_Actions",
        canClose: true
      });
    }
  }
};

const actions = {
  doePendingRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    }: FilterPaged
  ) => {
    commit("setPendingList", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IDOEPending
    >> = await getPendingList({
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    });

    if (results.kind === "ok") commit("setPendingList", results.data);
    else showErrorMessage(results.message);
  },

  doeProgrammedRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    }: FilterPaged
  ) => {
    commit("setProgrammedList", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IDOEProgrammed
    >> = await getProgrammedList({
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    });

    if (results.kind === "ok") commit("setProgrammedList", results.data);
    else showErrorMessage(results.message);
  },

  doePublishedRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    }: FilterPaged
  ) => {
    commit("setPublishedList", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IDOEPublished
    >> = await getPublishedList({
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    });

    if (results.kind === "ok") commit("setPublishedList", results.data);
    else showErrorMessage(results.message);
  },

  doeRefusedRequest: async (
    { commit }: ActionContext<State, RootState>,
    {
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    }: FilterPaged
  ) => {
    commit("setRefusedList", { count: 0, results: [] });
    const results: ResponseType<ResponsePaged<
      IDOERefused
    >> = await getRefusedList({
      page,
      order_by,
      paginator_count,
      search,
      filters,
      ignore_order_by_priority
    });

    if (results.kind === "ok") commit("setRefusedList", results.data);
    else showErrorMessage(results.message);
  },

  setToSentToPrintshop: async (
    _: ActionContext<State, RootState>,
    sentToPrintshopList: ISendToPrintshopDOE[]
  ) => {
    const results: ResponseGenericType = await setToSentToPrintshop(
      sentToPrintshopList
    );

    if (results.kind === "ok") {
      return results.data;
    }
    showErrorMessage(results.message);
  },

  doeReportListFileRequest: async (
    { dispatch }: ActionContext<State, RootState>,
    params: IReportListDOE
  ) => {
    showSuccessMessage({ message: General.TEXTS.EXPORTATION_STARTED });

    const results: ResponseType<IDownloadFileBatch> = await doeReportListFile(
      params
    );

    if (results.kind === "ok" && results.data) {
      if (params.separe_parts) {
        dispatch("startDownloadBatch", results.data);
      } else if (results.data.file)
        downloadWithURL(results.data.file, "relatório.pdf");
    } else showErrorMessage(results.message);

    return results.kind === "ok";
  },

  doeTrigger: ({ dispatch, state }: ActionContext<State, RootState>) => {
    const hasDoePending = state.doeSnackList.some(
      (doe: IDocumentDOE) => doe.status === "pending"
    );

    if (hasDoePending) {
      const doePending = state.doeSnackList.find(
        doe => doe.status === "pending"
      );
      doePending && dispatch("doeObjectAction", doePending);
    }
  },

  doeObjectAction: async (
    { dispatch, commit }: ActionContext<State, RootState>,
    doeObject: IDocumentDOE
  ) => {
    let results;
    doeObject.status = "processing";
    if (
      [doeAction.CANCEL_PROGRAM, doeAction.PROGRAM].includes(
        doeObject.type as any
      )
    )
      results = await programPublish([doeObject]);
    else if (doeObject.type === doeAction.REFUSE)
      results = await refusePublish([doeObject]);
    else results = await publish([doeObject]);

    if (results.kind === "ok") doeObject.status = "done";
    else doeObject.status = "error";

    commit("checkDoneDoeActions");
    dispatch("doeTrigger");
  },

  setCancelDoeAction: (
    { state }: ActionContext<State, RootState>,
    listIndex: number
  ) => {
    if (listIndex === -1) {
      state.doeSnackList.forEach((doe: IDocumentDOE) => {
        if (doe.status === "pending") {
          doe.status = "abort";
        }
      });
    } else if (state.doeSnackList[listIndex].status === "pending")
      state.doeSnackList[listIndex].status = "abort";
  },

  createModelDocumentEdoweb: async (
    _: ActionContext<State, RootState>,
    {
      document_json,
      name,
      short_name,
      document_type,
      all_parts,
      parts,
      nature,
      use_type
    }: IUpdateDOE
  ) => {
    const results: ResponseGenericType = await createModelDocumentEdoweb({
      document_json,
      name,
      short_name,
      all_parts,
      parts,
      document_type,
      nature,
      use_type
    });

    if (results.kind === "ok")
      showSuccessMessage({ message: General.SUCCESS_CREATED });
    else showErrorMessage(results.message);

    return results.kind === "ok";
  },

  updateModelDocumentEdoweb: async (
    _: ActionContext<State, RootState>,
    {
      document_json,
      id,
      name,
      short_name,
      document_type,
      all_parts,
      parts,
      nature,
      use_type
    }: ISimpleDOE
  ) => {
    const results: ResponseGenericType = await updateModelDocumentEdoweb({
      document_json,
      id,
      name,
      short_name,
      all_parts,
      parts,
      document_type,
      nature,
      use_type
    });

    if (results.kind === "ok")
      showSuccessMessage({ message: General.SUCCESS_UPDATED });
    else showErrorMessage(results.message);

    return results.kind === "ok";
  },

  listModelDocumentEdoweb: async (
    { commit }: ActionContext<State, RootState>,
    searchParams: IRequestPageDOE
  ) => {
    const results: ResponseType<ResponsePaged<
      ICompleteDOE
    >> = await listModelDocumentEdoweb(searchParams);

    if (results.kind === "ok") commit("setListDocumentEdoweb", results.data);
    else showErrorMessage(results.message);
  },

  listModelDocumentEdowebAdm: async (
    { commit, getters }: ActionContext<State, RootState>,
    searchParams: IRequestPageDOE
  ) => {
    const results: ResponseType<ResponsePaged<
      IListDOE
    >> = await listModelDocumentEdowebAdm(searchParams);

    if (results.kind === "ok")
      commit("setListDocumentEdowebAdm", {
        ...results.data,
        results: results.data?.results?.map(doc => ({
          ...doc,
          parts: doc.part_capacity.map(getters.getPartsById)
        }))
      });
    else showErrorMessage(results.message);
  },

  getModelDocumentEdowebById: async (
    { commit }: ActionContext<State, RootState>,
    id: string
  ) => {
    const results: ResponseType<ICompleteDOE> = await getModelDocumentEdoweb(
      id
    );

    if (results.kind === "ok") commit("setCurrentDocumentEdoweb", results.data);
    else if (results.kind === "Not Found")
      showErrorMessage(General.MESSAGES.NO_RECORDS_FOUND);
    else showErrorMessage(results.message);

    return results.kind === "ok";
  },

  deleteModelDocumentEdowebById: async (
    _: ActionContext<State, RootState>,
    id: string
  ) => {
    const results: ResponseGenericType = await deleteModelDocumentEdoweb(id);

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  activeModelDocumentEdowebById: async (
    _: ActionContext<State, RootState>,
    { id, active }: IActiveDOE
  ) => {
    const results: ResponseGenericType = await activeModelDocumentEdoweb({
      id,
      active
    });

    if (results.kind !== "ok") showErrorMessage(results.message);
  },

  validateDocumentEdowebAction: async (
    _: ActionContext<State, RootState>,
    data: ITableValidateDOE
  ) => {
    const results: ResponseGenericType = await validateDocumentEdoweb(data);

    if (results.kind === "ok") return results.data;
    showErrorMessage(results.message);
  },

  doeRefusePublish: async (
    _: ActionContext<State, RootState>,
    listDocuments: IDocumentDOE[]
  ) => {
    const results: ResponseGenericType = await refusePublish(listDocuments);

    if (results.kind === "ok" && results.data?.message)
      showSuccessMessage({ message: results.data.message });
    else showErrorMessage(results.message);
  },

  doeProgramPublish: async (
    _: ActionContext<State, RootState>,
    listDocuments: IDocumentDOE[]
  ) => {
    const results: ResponseGenericType = await programPublish(listDocuments);

    if (results.kind === "ok" && results.data?.message)
      showSuccessMessage({ message: results.data.message });
    else showErrorMessage(results.message);
    return results.kind === "ok";
  },

  doePublish: async (
    _: ActionContext<State, RootState>,
    listDocuments: IDocumentDOE[]
  ) => {
    const results: ResponseGenericType = await publish(listDocuments);

    if (results.kind === "ok" && results.data?.message)
      showSuccessMessage({ message: results.data.message });
    else showErrorMessage(results.message);
  }
};

const getters = {
  getDOEPending: (state: State) => ({
    list: state.pendingList,
    count: state.pendingListCount
  }),
  getDOEProgrammed: (state: State) => ({
    list: state.programmedList,
    count: state.programmedListCount
  }),
  getDOEPublished: (state: State) => ({
    list: state.publishedList,
    count: state.publishedListCount
  }),
  getDOERefused: (state: State) => ({
    list: state.refusedList,
    count: state.refusedListCount
  }),
  getEdowebDocument: (state: State) => ({
    list: state.documentEdoweb,
    count: state.documentEdowebCount
  }),
  getEdowebDocumentAdm: (state: State) => ({
    list: state.documentEdowebAdm,
    count: state.documentEdowebAdmCount
  }),
  getCurrentDocumentEdoweb: (state: State) => state.currentDocumentEdoweb,
  getDoeSnackList: (state: State) => state.doeSnackList,
  getDoePageUpdater: (state: State) => state.doePageNeedsUpdate
};

export default { state, mutations, actions, getters };
